import { feedbackApiChatFeedbackSessionIdMessageIdPut, type FeedbackValue } from "@noa/api-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reportError } from "../typed-client";
import { ApiError } from "../errors";
import { MessageQueryKey } from "../query-keys";

interface UseMessageFeedbackArgs {
  sessionId: string;
  messageId: string;
}

interface SendFeedbackArgs {
  sessionId: string;
  messageId: string;
  feedback: FeedbackValue;
}

const postFeedback = async ({ sessionId, messageId, feedback }: SendFeedbackArgs) => {
  await feedbackApiChatFeedbackSessionIdMessageIdPut({
    path: { session_id: sessionId, message_id: messageId },
    body: { feedback },
    throwOnError: true,
  });
};

/**
 * Sends a feedback value for one message and invalidates that message's query.
 *
 * It deliberately does NOT write the feedback into the store — this package is store-free. The
 * caller already holds the `feedback` it passed in, so it writes the store itself via
 * `useSetMessageFeedback` from `@noa/client-store` once the mutation resolves.
 */
export const useMessageFeedback = ({ sessionId, messageId }: UseMessageFeedbackArgs) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, isError, error, isSuccess, ...rest } = useMutation<void, ApiError, SendFeedbackArgs>({
    mutationFn: postFeedback,
    mutationKey: MessageQueryKey.sessionMessageDetail(sessionId, messageId),
    onError: reportError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: MessageQueryKey.sessionMessageDetail(sessionId, messageId),
      });
    },
  });

  return {
    sendMessageFeedback: mutateAsync,
    isSendingFeedback: isPending,
    isErrorSendingFeedback: isError,
    errorSendingFeedback: error,
    isSuccessSendingFeedback: isSuccess,
    ...rest,
  };
};
